import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Button,
  Modal,
  Form,
  DatePicker,
  Input,
  Table,
  Alert,
  Spin,
  Typography,
  Tag,
  Empty,
  Popconfirm,
  message
} from 'antd'
import { SendOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

const ORDER_LINE_ITEMS_QUERY = gql`
  query DispatchRequestOrder($number: String!) {
    order(number: $number) {
      id
      lineItems(limit: -1) {
        id
        name
        quantity
      }
    }
  }
`

const ORDER_DELIVERY_STATUS_QUERY = gql`
  query DispatchOrderDeliveryStatus($orderId: ID!) {
    dispatchOrderDeliveryStatus(orderId: $orderId) {
      state
      unassignedRequestCount
      deliveryRequests {
        id
        externalId
        derivedState
        requestedDateFrom
        requestedDateTo
        preferredDate
        note
        items {
          id
          quantity
          lineItem {
            id
            name
          }
        }
      }
    }
  }
`

const DISPATCH_REQUEST_DELIVERY_MUTATION = gql`
  mutation DispatchRequestDelivery($input: DispatchRequestDeliveryInput!) {
    dispatchRequestDelivery(input: $input) {
      deliveryRequest {
        id
      }
      errors {
        message
        key
      }
    }
  }
`

const DISPATCH_CANCEL_DELIVERY_REQUEST_MUTATION = gql`
  mutation DispatchCancelDeliveryRequest($externalId: String!, $reason: String) {
    dispatchCancelDeliveryRequest(externalId: $externalId, reason: $reason) {
      deliveryRequest {
        id
        derivedState
      }
      errors {
        message
        key
      }
    }
  }
`

const DISPATCH_UPDATE_DELIVERY_REQUEST_MUTATION = gql`
  mutation DispatchUpdateDeliveryRequest(
    $externalId: String!
    $input: DispatchUpdateDeliveryRequestInput!
  ) {
    dispatchUpdateDeliveryRequest(externalId: $externalId, input: $input) {
      deliveryRequest {
        id
      }
      errors {
        message
        key
      }
    }
  }
`

const STATE_COLORS = {
  unassigned: 'default',
  planned: 'blue',
  dispatched: 'gold',
  delivered: 'green',
  invoiced: 'cyan',
  paid: 'green',
  cancelled: 'red'
}

const STATE_LABELS = {
  unassigned: 'ยังไม่จัดรถ',
  planned: 'จัดรถแล้ว',
  dispatched: 'กำลังจัดส่ง',
  delivered: 'ส่งสำเร็จ',
  invoiced: 'ออกใบแจ้งหนี้',
  paid: 'ชำระแล้ว',
  cancelled: 'ยกเลิก'
}

const formatDateRange = (from, to) => {
  if (from === to) return from
  return `${from} → ${to}`
}

const itemLabel = (item) => {
  const name = (item.lineItem && item.lineItem.name) || '-'
  return `${name} × ${item.quantity}`
}

const MUTABLE_STATES = new Set(['unassigned', 'planned'])

const buildRequestColumns = ({ onCancel, onEdit, cancellingId }) => [
  {
    title: 'วันที่ต้องการ',
    key: 'date',
    render: (_, r) => (
      <div>
        <div>{formatDateRange(r.requestedDateFrom, r.requestedDateTo)}</div>
        {r.preferredDate && (
          <Typography.Text type='secondary' style={{ fontSize: 12 }}>
            ต้องการ: {r.preferredDate}
          </Typography.Text>
        )}
      </div>
    )
  },
  {
    title: 'สถานะ',
    dataIndex: 'derivedState',
    key: 'state',
    width: 120,
    render: (state) => (
      <Tag color={STATE_COLORS[state] || 'default'}>
        {STATE_LABELS[state] || state}
      </Tag>
    )
  },
  {
    title: 'รายการ',
    key: 'items',
    render: (_, r) => (
      <div>
        {r.items.map((it) => (
          <div key={it.id}>{itemLabel(it)}</div>
        ))}
      </div>
    )
  },
  {
    title: 'หมายเหตุ',
    dataIndex: 'note',
    key: 'note',
    render: (note) => note || '-'
  },
  {
    title: '',
    key: 'actions',
    width: 140,
    render: (_, r) => {
      if (!MUTABLE_STATES.has(r.derivedState)) return null
      if (!r.externalId) return null
      return (
        <span>
          <Button size='small' type='link' onClick={() => onEdit(r)}>
            แก้ไข
          </Button>
          <Popconfirm
            title='ยกเลิกคำขอจัดส่ง?'
            okText='ยกเลิก'
            cancelText='ไม่'
            okButtonProps={{ danger: true }}
            onConfirm={() => onCancel(r.externalId)}
          >
            <Button
              danger
              size='small'
              type='link'
              loading={cancellingId === r.externalId}
            >
              ยกเลิก
            </Button>
          </Popconfirm>
        </span>
      )
    }
  }
]

const EditDeliveryRequestForm = ({
  request,
  orderDbId,
  onSuccess,
  onCancel
}) => {
  const [form] = Form.useForm()
  const [errorsMessage, setErrors] = useState(null)

  const [updateDeliveryRequest, { loading: saving }] = useMutation(
    DISPATCH_UPDATE_DELIVERY_REQUEST_MUTATION,
    {
      refetchQueries: orderDbId
        ? [
          {
            query: ORDER_DELIVERY_STATUS_QUERY,
            variables: { orderId: orderDbId }
          }
        ]
        : [],
      awaitRefetchQueries: true,
      onCompleted: ({ dispatchUpdateDeliveryRequest }) => {
        const { errors } = dispatchUpdateDeliveryRequest
        if (errors && errors.length > 0) {
          setErrors(errors.map((e) => e.message).join(', '))
          return
        }
        setErrors(null)
        message.success('แก้ไขคำขอจัดส่งสำเร็จ')
        onSuccess()
      },
      onError: (err) => setErrors(err.message)
    }
  )

  const onFinish = (values) => {
    const [from, to] = values.requestedDateRange
    const items = request.items
      .filter((it) => it.lineItem)
      .map((it) => {
        const qty = (values.quantities || {})[it.id]
        return {
          lineItemId: it.lineItem.id,
          quantity: qty != null ? Number(qty) : it.quantity
        }
      })
      .filter((it) => it.quantity > 0)

    updateDeliveryRequest({
      variables: {
        externalId: request.externalId,
        input: {
          requestedDateFrom: from.format('YYYY-MM-DD'),
          requestedDateTo: to.format('YYYY-MM-DD'),
          preferredDate: values.preferredDate
            ? values.preferredDate.format('YYYY-MM-DD')
            : null,
          note: values.note || null,
          items
        }
      }
    })
  }

  const itemColumns = [
    {
      title: 'สินค้า',
      key: 'name',
      render: (_, r) => (r.lineItem && r.lineItem.name) || '-'
    },
    {
      title: 'จำนวน',
      key: 'quantity',
      width: 140,
      render: (_, r) => (
        <Form.Item
          name={['quantities', r.id]}
          initialValue={r.quantity}
          rules={[{ required: true, message: 'กรุณาระบุจำนวน' }]}
          style={{ marginBottom: 0 }}
        >
          <Input type='number' min={0} step='any' />
        </Form.Item>
      )
    }
  ]

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinish}
      initialValues={{
        requestedDateRange: [
          dayjs(request.requestedDateFrom),
          dayjs(request.requestedDateTo)
        ],
        preferredDate: request.preferredDate ? dayjs(request.preferredDate) : null,
        note: request.note || ''
      }}
    >
      {errorsMessage && (
        <Alert
          type='error'
          message={errorsMessage}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form.Item
        label='ช่วงวันที่ต้องการจัดส่ง'
        name='requestedDateRange'
        rules={[{ required: true, message: 'กรุณาเลือกช่วงวันที่' }]}
      >
        <DatePicker.RangePicker style={{ width: '100%' }} format='YYYY-MM-DD' />
      </Form.Item>
      <Form.Item label='วันที่ต้องการ (ถ้ามี)' name='preferredDate'>
        <DatePicker style={{ width: '100%' }} format='YYYY-MM-DD' />
      </Form.Item>
      <Form.Item label='หมายเหตุ' name='note'>
        <Input.TextArea rows={2} />
      </Form.Item>
      <Table
        rowKey='id'
        size='small'
        dataSource={request.items}
        columns={itemColumns}
        pagination={false}
        style={{ marginBottom: 16 }}
      />
      <div style={{ textAlign: 'right' }}>
        <Button onClick={onCancel} style={{ marginRight: 8 }}>
          ยกเลิก
        </Button>
        <Button type='primary' htmlType='submit' loading={saving}>
          บันทึก
        </Button>
      </div>
    </Form>
  )
}

EditDeliveryRequestForm.propTypes = {
  request: PropTypes.object.isRequired,
  orderDbId: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

EditDeliveryRequestForm.defaultProps = {
  orderDbId: null
}

export const DispatchRequestList = ({ orderId }) => {
  const [cancellingId, setCancellingId] = useState(null)
  const [editingRequest, setEditingRequest] = useState(null)

  const { loading: orderLoading, data: orderData } = useQuery(
    ORDER_LINE_ITEMS_QUERY,
    {
      variables: { number: orderId },
      fetchPolicy: 'cache-and-network'
    }
  )

  const orderDbId = orderData && orderData.order && orderData.order.id

  const { data, loading } = useQuery(ORDER_DELIVERY_STATUS_QUERY, {
    variables: { orderId: orderDbId },
    skip: !orderDbId,
    fetchPolicy: 'cache-and-network'
  })

  const [cancelDeliveryRequest] = useMutation(
    DISPATCH_CANCEL_DELIVERY_REQUEST_MUTATION,
    {
      refetchQueries: orderDbId
        ? [
          {
            query: ORDER_DELIVERY_STATUS_QUERY,
            variables: { orderId: orderDbId }
          }
        ]
        : [],
      awaitRefetchQueries: true
    }
  )

  const handleCancel = async (externalId) => {
    setCancellingId(externalId)
    try {
      const { data: result } = await cancelDeliveryRequest({
        variables: { externalId, reason: null }
      })
      const errors =
        (result &&
          result.dispatchCancelDeliveryRequest &&
          result.dispatchCancelDeliveryRequest.errors) ||
        []
      if (errors.length > 0) {
        message.error(errors.map((e) => e.message).join(', '))
      } else {
        message.success('ยกเลิกคำขอจัดส่งสำเร็จ')
      }
    } catch (err) {
      message.error(err.message)
    } finally {
      setCancellingId(null)
    }
  }

  if ((orderLoading && !orderData) || (loading && !data)) return <Spin />

  const deliveryRequests =
    (data &&
      data.dispatchOrderDeliveryStatus &&
      data.dispatchOrderDeliveryStatus.deliveryRequests) ||
    []

  return (
    (<React.Fragment>
      {deliveryRequests.length === 0 ? (
        <Empty description='ยังไม่มีคำขอจัดส่ง' />
      ) : (
        <Table
          rowKey='id'
          size='small'
          dataSource={deliveryRequests}
          columns={buildRequestColumns({
            onCancel: handleCancel,
            onEdit: setEditingRequest,
            cancellingId
          })}
          pagination={false}
        />
      )}
      <Modal
        title='แก้ไขคำขอจัดส่ง'
        open={!!editingRequest}
        footer={null}
        width={720}
        destroyOnClose
        onCancel={() => setEditingRequest(null)}
      >
        {editingRequest && (
          <EditDeliveryRequestForm
            request={editingRequest}
            orderDbId={orderDbId}
            onSuccess={() => setEditingRequest(null)}
            onCancel={() => setEditingRequest(null)}
          />
        )}
      </Modal>
    </React.Fragment>)
  );
}

DispatchRequestList.propTypes = {
  orderId: PropTypes.string.isRequired
}

const DispatchRequestForm = ({ orderId, onSuccess, onCancel }) => {
  const [form] = Form.useForm()
  const [errorsMessage, setErrors] = useState(null)

  const { loading, data } = useQuery(ORDER_LINE_ITEMS_QUERY, {
    variables: { number: orderId },
    fetchPolicy: 'cache-and-network'
  })

  const orderDbId = data && data.order && data.order.id

  const [requestDelivery, { loading: saving }] = useMutation(
    DISPATCH_REQUEST_DELIVERY_MUTATION,
    {
      refetchQueries: orderDbId
        ? [
          {
            query: ORDER_DELIVERY_STATUS_QUERY,
            variables: { orderId: orderDbId }
          }
        ]
        : [],
      awaitRefetchQueries: true,
      onCompleted: ({ dispatchRequestDelivery }) => {
        const { errors } = dispatchRequestDelivery
        if (errors && errors.length > 0) {
          setErrors(errors.map((e) => e.message).join(', '))
          return
        }
        setErrors(null)
        message.success('สร้างคำขอจัดส่งสำเร็จ')
        onSuccess()
      },
      onError: (err) => setErrors(err.message)
    }
  )

  const lineItems = (data && data.order && data.order.lineItems) || []

  const onFinish = (values) => {
    if (!orderDbId) {
      setErrors('ยังโหลดข้อมูลออเดอร์ไม่เสร็จ กรุณาลองใหม่')
      return
    }

    const items = lineItems
      .filter((item) => item.quantity > 0)
      .map((item) => ({
        lineItemId: item.id,
        quantity: item.quantity
      }))

    if (items.length === 0) {
      setErrors('ไม่มีรายการสินค้าสำหรับจัดส่ง')
      return
    }

    const [from, to] = values.requestedDateRange
    requestDelivery({
      variables: {
        input: {
          orderId: orderDbId,
          requestedDateFrom: from.format('YYYY-MM-DD'),
          requestedDateTo: to.format('YYYY-MM-DD'),
          preferredDate: values.preferredDate
            ? values.preferredDate.format('YYYY-MM-DD')
            : null,
          note: values.note || null,
          items
        }
      }
    })
  }

  if (loading && !data) return <Spin />

  const columns = [
    {
      title: 'สินค้า',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'จำนวนจัดส่ง',
      dataIndex: 'quantity',
      key: 'dispatchQuantity',
      width: 140
    }
  ]

  return (
    <Form form={form} layout='vertical' onFinish={onFinish}>
      {errorsMessage && (
        <Alert
          type='error'
          message={errorsMessage}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form.Item
        label='ช่วงวันที่ต้องการจัดส่ง'
        name='requestedDateRange'
        rules={[{ required: true, message: 'กรุณาเลือกช่วงวันที่' }]}
        initialValue={[dayjs().add(1, 'day'), dayjs().add(3, 'day')]}
      >
        <DatePicker.RangePicker style={{ width: '100%' }} format='YYYY-MM-DD' />
      </Form.Item>
      <Form.Item label='วันที่ต้องการ (ถ้ามี)' name='preferredDate'>
        <DatePicker style={{ width: '100%' }} format='YYYY-MM-DD' />
      </Form.Item>
      <Form.Item label='หมายเหตุ' name='note'>
        <Input.TextArea rows={2} />
      </Form.Item>
      <Table
        rowKey='id'
        size='small'
        dataSource={lineItems}
        columns={columns}
        pagination={false}
        style={{ marginBottom: 16 }}
      />
      <div style={{ textAlign: 'right' }}>
        <Button onClick={onCancel} style={{ marginRight: 8 }}>
          ยกเลิก
        </Button>
        <Button type='primary' htmlType='submit' loading={saving}>
          ส่งคำขอจัดส่ง
        </Button>
      </div>
    </Form>
  )
}

DispatchRequestForm.propTypes = {
  orderId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

const DispatchRequest = ({ orderId }) => {
  const [visible, setVisible] = useState(false)

  return (
    (<div>
      <Typography.Link onClick={() => setVisible(true)}>
        <SendOutlined /> ขอจัดส่ง
      </Typography.Link>
      <Modal
        title='ขอจัดส่งสินค้า'
        open={visible}
        footer={null}
        width={800}
        destroyOnClose
        onCancel={() => setVisible(false)}
      >
        <DispatchRequestForm
          orderId={orderId}
          onSuccess={() => setVisible(false)}
          onCancel={() => setVisible(false)}
        />
      </Modal>
    </div>)
  );
}

DispatchRequest.propTypes = {
  orderId: PropTypes.string.isRequired
}

export default DispatchRequest
